import { Rule } from "../models/Rule";
import { RuleField } from "../models/RuleField";
import { RuleSchema } from "../models/RuleSchema";
import { RuleSchemaUiFieldGroup } from "../models/RuleSchemaUi";
import { RuleVisibilityFieldTypeValues } from "../models/RuleFieldTypeArgs";
import { DataCleaningService } from "./DataCleaningService";
import { DataCleaningTypeGuardService } from "./DataCleaningTypeGuardService";

export class DataCleaningUiService {

    public static getRuleSchemaUiFieldGroups = (ruleSchema: RuleSchema): Array<RuleSchemaUiFieldGroup> | null => {

        if (!DataCleaningTypeGuardService.hasDefinedRuleSchemaUi(ruleSchema)) return null;
        return ruleSchema.ui?.field_groups as Array<RuleSchemaUiFieldGroup>;

    }

    public static getValueByPath = (configuration: any, path?: string | string[]): any => {
        if (configuration == null) return undefined;
        if (path === undefined || path === null) return undefined;

        const parts: string[] = Array.isArray(path)
            ? path
            : (String(path).match(/[^.[\]]+/g) || []);

        if (parts.length === 0) return configuration;

        let cur: any = configuration;
        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            if (cur == null) return undefined;

            if (part === '*') {
                if (!Array.isArray(cur)) return undefined;
                const remainingParts = parts.slice(i + 1);
                return cur.map((item: any) => DataCleaningUiService.getValueByPath(item, remainingParts));
            }

            if (/^\d+$/.test(part)) {
                cur = cur[Number(part)];
            } else {
                cur = cur[part];
            }
        }
        return cur;
    }

    public static isFieldVisibleFromRoot = (field: RuleField, rootConfiguration?: Rule | null, _currentPath?: string[]) => {

        if (field.visible === false) return false;
        if (rootConfiguration != null) {
            const flattenedRootConfiguration = DataCleaningService.getFlattenedRule(rootConfiguration, null);

            const visibleConfigs = field?.field_type_args?.visible_configs;
            if (Array.isArray(visibleConfigs)) {

                let visible = false;
                visibleConfigs.forEach((config: { visible_prop_path: string, visible_prop_value: any, visible_value?: RuleVisibilityFieldTypeValues, visible_parent?: string }) => {
                    const propPath = config?.visible_prop_path;
                    const expected = config?.visible_prop_value;
                    const expectedValueType = config?.visible_value;
                    const visibleParent = config?.visible_parent;

                    let targetPathParts: string[] = [];

                    if (visibleParent && Array.isArray(_currentPath)) {
                        let foundIndex = -1;
                        for (let i = _currentPath.length - 1; i >= 0; i--) {
                            if (_currentPath[i] === visibleParent && i + 1 < _currentPath.length && /^\d+$/.test(String(_currentPath[i + 1]))) {
                                foundIndex = i;
                                break;
                            }
                        }
                        if (foundIndex !== -1) {
                            const base = _currentPath.slice(0, foundIndex + 2);
                            const propParts = propPath.includes('.') ? propPath.split('.') : [propPath];
                            targetPathParts = [...base, ...propParts];
                        }
                    }

                    if (targetPathParts.length === 0) {
                        targetPathParts = propPath.includes('.')
                            ? propPath.split('.')
                            : (_currentPath ? [..._currentPath.slice(0, -1), propPath] : [propPath]);
                    }

                    const val = DataCleaningUiService.getValueByPath(flattenedRootConfiguration, targetPathParts);
                    const flatVal = Array.isArray(val) ? val.flat(Infinity) : val;
                    const matches = Array.isArray(flatVal)
                        ? flatVal.some((v: any) => DataCleaningUiService.valueMatchesRuleVisibilityFieldTypeValue(v, expectedValueType, expected) || v === expected)
                        : DataCleaningUiService.valueMatchesRuleVisibilityFieldTypeValue(flatVal, expectedValueType, expected) || flatVal === expected;
                    visible = visible || matches;
                })
                return visible;
            }
        }
        return true;
    }

    private static valueMatchesRuleVisibilityFieldTypeValue(value: any, visibilityValue?: RuleVisibilityFieldTypeValues, expected?: any): boolean {

        switch (visibilityValue) {
            case RuleVisibilityFieldTypeValues.Defined:
                return value !== undefined;
            case RuleVisibilityFieldTypeValues.Falsey:
                return !value;
            case RuleVisibilityFieldTypeValues.Truthy:
                return !!value;
            case RuleVisibilityFieldTypeValues.Contains:
                if (Array.isArray(expected) || typeof expected === 'string') {
                    return expected.includes(value);
                }
                console.warn(`DataCleaningUiService: visible_value 'contains' requires visible_prop_value to be an array or string, got: ${typeof expected}`);
                return false;
            case RuleVisibilityFieldTypeValues.DoesNotContain:
                if (Array.isArray(expected) || typeof expected === 'string') {
                    return !expected.includes(value);
                }
                console.warn(`DataCleaningUiService: visible_value 'does_not_contain' requires visible_prop_value to be an array or string, got: ${typeof expected}`);
                return false;
            default:
                return false;
        }

    }

}
